import { Alert, Box } from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  ListRequestOfSkuContentsFilter,
  ListResponseOfSkuContentItem,
  SkuContentsFilter,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { SkuFormProps } from "types/sku-related-types";
import { SkuContentFilter } from "./SkuContentFilter";

type SkuContentsProps = {
  readonly?: boolean;
};

export function SkuContents({
  sku,
  readonly = false,
  onSkuUpdated,
  onDirty,
  onClean,
  ...rest
}: SkuContentsProps & SkuFormProps) {
  const { skuService } = useGlobalContext();
  const { formatMessage: t } = useIntl();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("childSku.sku");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const searchTerm = persistanceStore.useState((s) => s.skuContentSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const ownerOrganizationId = persistanceStore.useState(
    (s) => s.skuContentOwnerOrganizationId,
  );

  const minPrice = persistanceStore.useState((s) => s.skuContentMinPrice);
  const maxPrice = persistanceStore.useState((s) => s.skuContentMaxPrice);
  const debouncedMinPrice = useDebounce(minPrice, 500);
  const debouncedMaxPrice = useDebounce(maxPrice, 500);

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfSkuContentItem>(
    [
      "skuContents",
      sku.id!,
      page,
      rowsPerPage,
      order,
      orderBy,
      debouncedSearchTerm,
      ownerOrganizationId,
      debouncedMinPrice,
      debouncedMaxPrice,
    ],
    () =>
      skuService.getSkuContents(
        sku.id!,
        ListRequestOfSkuContentsFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: SkuContentsFilter.fromJS({
            ownerOrganizationId,
            minPrice: debouncedMinPrice,
            maxPrice: debouncedMaxPrice,
            searchText: debouncedSearchTerm,
          }),
        }),
      ),
  );

  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearchTerm,
    ownerOrganizationId,
    debouncedMinPrice,
    debouncedMaxPrice,
  ]);

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "sku",
      customSortAccessor: "childSku.sku",
      align: "left",
      type: "text",
      label: t({ id: "skuLabel" }),
    },
    {
      accessor: "productName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "productLabel" }),
    },
    {
      accessor: "ownerOrganizationName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "ownerOrganization" }),
    },
    {
      accessor: "price",
      customSortAccessor: "childSku.price",
      align: "left",
      width: 0,
      type: "price",
      label: t({ id: "skuContentPriceLabel" }),
    },
    {
      accessor: "accessTypeName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "skuContentAccessTypeLabel" }),
    },
  ];

  return (
    <Box>
      <SkuContentFilter
        ownerOrganizationId={ownerOrganizationId}
        maxPrice={maxPrice}
        minPrice={minPrice}
        searchTerm={searchTerm}
      />
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: data?.data ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "skuContentsTitle" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />
    </Box>
  );
}
